(() => {
  'use strict';

  if (window.__mmRevenueEngineV4) return;
  window.__mmRevenueEngineV4 = true;

  function normalizeRoot(candidate) {
    const value = String(candidate || '').trim();
    if (!value || !value.startsWith('/') || value.startsWith('//')) return '';
    return value.replace(/\/+$/, '') || '/';
  }

  function pathLocaleRoot() {
    const language = String(document.documentElement.lang || '').toLowerCase().split('-')[0];
    const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
    if (language && firstPathSegment.toLowerCase() === language) return `/${firstPathSegment}`;
    return '/';
  }

  function currentLocaleRoot() {
    const pathRoot = pathLocaleRoot();
    const candidates = [window.Shopify?.routes?.root, window.routes?.root_url];
    let rootFallback = '';

    for (const candidate of candidates) {
      const root = normalizeRoot(candidate);
      if (!root) continue;
      if (root !== '/') return root;
      rootFallback = '/';
    }

    return pathRoot !== '/' ? pathRoot : (rootFallback || '/');
  }

  function localizedInternalUrl(rawHref) {
    const href = String(rawHref || '').trim();
    const localeRoot = currentLocaleRoot();
    if (!href.startsWith('/') || href.startsWith('//') || localeRoot === '/') return href;
    if (
      href === localeRoot ||
      href.startsWith(`${localeRoot}/`) ||
      href.startsWith(`${localeRoot}?`) ||
      href.startsWith(`${localeRoot}#`)
    ) return href;
    return `${localeRoot}${href}`;
  }

  function localizeInternalLinks(root) {
    root.querySelectorAll('a[href^="/"]').forEach((link) => {
      const href = link.getAttribute('href');
      const localized = localizedInternalUrl(href);
      if (localized && localized !== href) link.setAttribute('href', localized);
    });
  }

  function localizeOperationalStates(root) {
    const language = String(document.documentElement.lang || '').toLowerCase().split('-')[0];
    if (language !== 'es') return;

    const labels = Object.freeze({
      existing: 'Existente',
      saved: 'Guardado',
      draft: 'Borrador',
      research: 'Investigación',
      blocked: 'Bloqueado',
      blocked_not_certified: 'Bloqueado · no certificado',
      pass: 'Aprobado',
      pass_current_snapshot: 'Aprobado · instantánea actual',
      partial_evidence: 'Evidencia parcial',
      not_run: 'No ejecutado',
      external_not_verified: 'Externo · no verificado',
      safe_inactive: 'Inactivo de forma segura'
    });

    root.querySelectorAll('.mm-revenue__state').forEach((state) => {
      const key = (state.textContent || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
      if (labels[key]) state.textContent = labels[key];
    });
  }

  function analyticsAllowed() {
    try {
      const shopify = window.Shopify;
      const privacy = shopify?.customerPrivacy;
      return Boolean(
        shopify?.theme &&
        String(shopify.theme.role).toLowerCase() === 'main' &&
        !shopify.designMode &&
        !/[?&](?:preview_theme_id|_fd|pb)=/.test(window.location.search) &&
        typeof privacy?.analyticsProcessingAllowed === 'function' &&
        privacy.analyticsProcessingAllowed() === true &&
        typeof shopify.analytics?.publish === 'function'
      );
    } catch (_) {
      return false;
    }
  }

  function track(action, metadata = {}) {
    if (!analyticsAllowed()) return;
    try {
      const result = window.Shopify.analytics.publish('mm:revenue_engine_action', {
        action,
        event_id: window.crypto?.randomUUID?.() || `mm-revenue-${Date.now()}`,
        page_path: window.location.pathname.slice(0, 300),
        ...metadata
      });
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (_) {}
  }

  function hardenForm(form) {
    form.querySelectorAll('input[type="text"]').forEach((field) => {
      if (!field.maxLength || field.maxLength < 0) field.maxLength = 160;
    });
    form.querySelectorAll('input[type="email"]').forEach((field) => {
      if (!field.maxLength || field.maxLength < 0) field.maxLength = 254;
    });
    form.querySelectorAll('input[type="tel"]').forEach((field) => {
      if (!field.maxLength || field.maxLength < 0) field.maxLength = 32;
    });
    form.querySelectorAll('input[type="url"]').forEach((field) => {
      if (!field.maxLength || field.maxLength < 0) field.maxLength = 2048;
    });
    form.querySelectorAll('textarea').forEach((field) => {
      if (!field.maxLength || field.maxLength < 0) field.maxLength = 5000;
    });

    const status = form.querySelector('[data-mm-revenue-form-status]');
    if (status) {
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
    }
  }

  function initialize(scope = document) {
    const roots = [];
    if (scope.matches?.('[data-mm-revenue-root]')) roots.push(scope);
    if (typeof scope.querySelectorAll === 'function') {
      roots.push(...scope.querySelectorAll('[data-mm-revenue-root]'));
    }

    for (const root of roots) {
      if (root.dataset.mmRevenueReady === 'true') continue;
      root.dataset.mmRevenueReady = 'true';
      localizeInternalLinks(root);
      localizeOperationalStates(root);

      root.querySelectorAll('a[data-mm-revenue-track]').forEach((link) => {
        link.addEventListener('click', () => {
          track(link.dataset.mmRevenueTrack || 'link_click', {
            destination: link.getAttribute('href') || '',
            label: (link.textContent || '').trim().slice(0, 120)
          });
        });
      });

      root.querySelectorAll('details').forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        const sync = () => summary?.setAttribute('aria-expanded', String(details.open));
        details.addEventListener('toggle', sync);
        sync();
      });

      root.querySelectorAll('form[data-mm-revenue-form]').forEach((form) => {
        if (form.dataset.mmRevenueFormReady === 'true') return;
        form.dataset.mmRevenueFormReady = 'true';
        hardenForm(form);
        let submitting = false;

        form.addEventListener('submit', (event) => {
          if (submitting) {
            event.preventDefault();
            return;
          }

          const rights = form.querySelector('[data-mm-revenue-rights]');
          if (rights && !rights.checked) {
            event.preventDefault();
            rights.focus();
            const status = form.querySelector('[data-mm-revenue-form-status]');
            if (status) {
              status.hidden = false;
              status.setAttribute('role', 'alert');
              status.setAttribute('aria-live', 'assertive');
              status.className = 'mm-revenue__form-status mm-revenue__form-status--error';
              status.textContent = form.dataset.mmRevenueRightsError ||
                'Confirm that you own the submitted material or have permission to submit it.';
            }
            return;
          }

          submitting = true;
          form.setAttribute('aria-busy', 'true');
          const button = form.querySelector('button[type="submit"]');
          if (button) {
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
            button.dataset.mmOriginalLabel = button.textContent || '';
            button.textContent = form.dataset.mmRevenueSending || 'Sending…';
          }
          track('contact_submit', {
            pathway: form.dataset.mmRevenuePathway || ''
          });
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
